import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { trackMenuClick, trackCarnetDeSpots } from '../utils/analytics';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { name: 'Accueil', href: '/' },
    { name: 'Concept', href: '/concept' },
    { name: 'Comment ça marche', href: '/comment-ca-marche' },
    { name: 'Pourquoi Sun-Spot', href: '/pourquoi-sun-spot' },
    { name: 'Philosophie', href: '/philosophie' },
    { name: 'FAQ', href: '/faq' }
  ];

  const handleMenuClick = (itemName, isMobile = false) => {
    trackMenuClick(itemName, isMobile);
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  const handleCarnetClick = (isMobile = false) => {
    trackCarnetDeSpots(isMobile ? 'mobile_menu' : 'desktop_nav');
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };

  const handleMobileMenuToggle = () => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);
    trackMenuClick('hamburger_menu', true);
  };

  return (
    <header className="fixed top-0 w-full bg-white/70 backdrop-blur-md z-50 border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <picture>
              <source srcSet="/logo-64.webp" type="image/webp" />
              <img 
                src="/logo-64.png" 
                alt="Sun-Spot Logo" 
                className="w-8 h-8 rounded-lg object-cover"
                width="32"
                height="32"
              />
            </picture>
            <span className="text-2xl font-bold text-amber-800">Sun-Spot</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-4 xl:space-x-6">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleMenuClick(item.name)}
                className={`text-amber-700 hover:text-amber-900 transition-all duration-200 font-medium backdrop-blur-sm px-2 xl:px-3 py-2 rounded-lg hover:bg-white/30 text-sm xl:text-base whitespace-nowrap ${
                  location.pathname === item.href 
                    ? 'bg-white/50 text-amber-900 border-2 border-amber-300 shadow-md font-semibold' 
                    : 'hover:border hover:border-amber-200'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Carnet de Spots link - enhanced styling */}
            <Link
              to="/blog"
              onClick={() => handleCarnetClick(false)}
              className={`transition-all duration-200 font-medium backdrop-blur-sm px-2 xl:px-3 py-2 rounded-lg text-sm xl:text-base whitespace-nowrap ${
                location.pathname.startsWith('/blog') 
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-amber-900 border-2 border-orange-600 shadow-lg font-semibold' 
                  : 'bg-gradient-to-r from-yellow-300 to-orange-400 text-amber-800 hover:from-yellow-400 hover:to-orange-500 hover:text-amber-900 border border-orange-500 hover:border-orange-600 hover:shadow-md'
              }`}
            >
              Carnet de Spots
            </Link>
          </nav>

          {/* Medium Screen Navigation - All items with compact styling */}
          <nav className="hidden md:flex lg:hidden space-x-1 xl:space-x-2">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleMenuClick(item.name, true)}
                className={`text-amber-700 hover:text-amber-900 transition-all duration-200 font-medium backdrop-blur-sm px-1.5 py-1 rounded text-xs xl:text-sm whitespace-nowrap ${
                  location.pathname === item.href 
                    ? 'bg-white/50 text-amber-900 border border-amber-300 font-semibold' 
                    : 'hover:bg-white/30'
                }`}
              >
                {item.name === 'Comment ça marche' ? 'Comment ça marche' : 
                 item.name === 'Pourquoi Sun-Spot' ? 'Pourquoi' : item.name}
              </Link>
            ))}
            
            <Link
              to="/blog"
              onClick={() => handleCarnetClick(true)}
              className={`transition-all duration-200 font-medium backdrop-blur-sm px-1.5 py-1 rounded text-xs xl:text-sm whitespace-nowrap ${
                location.pathname.startsWith('/blog') 
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-amber-900 border border-orange-600 font-semibold' 
                  : 'bg-gradient-to-r from-yellow-300 to-orange-400 text-amber-800 hover:from-yellow-400 hover:to-orange-500 hover:text-amber-900 border border-orange-500'
              }`}
            >
              Carnet
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-amber-800 backdrop-blur-sm p-2 rounded-lg hover:bg-white/30"
            onClick={handleMobileMenuToggle}
            aria-label="Ouvrir le menu de navigation"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/80 backdrop-blur-md border-t border-white/20 py-4 rounded-b-lg">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleMenuClick(item.name, true)}
                className={`block w-full text-left px-4 py-3 text-amber-700 hover:text-amber-900 hover:bg-white/40 transition-all duration-200 font-medium ${
                  location.pathname === item.href 
                    ? 'bg-white/60 text-amber-900 border-l-4 border-amber-400 font-semibold' 
                    : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Carnet de Spots link - mobile with enhanced styling */}
            <Link
              to="/blog"
              onClick={() => handleCarnetClick(true)}
              className={`block w-full text-left px-4 py-3 transition-all duration-200 font-medium ${
                location.pathname.startsWith('/blog') 
                  ? 'bg-gradient-to-r from-yellow-300 to-orange-400 text-amber-900 border-l-4 border-orange-600 font-semibold' 
                  : 'bg-gradient-to-r from-yellow-200 to-orange-300 text-amber-800 hover:from-yellow-300 hover:to-orange-400 hover:text-amber-900 border-l-4 border-orange-400'
              }`}
            >
              Carnet de Spots
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;